import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import {
  Page,
  LayoutSingleColumn,
  PageContainer,
  PageHeader,
  Notice,
  EmptyState,
  LoadingPlaceholder,
  NamedLink,
} from '../../components';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { withRouter } from 'react-router-dom';
import FavoritesCardSection from './FavoritesCardSection';
import { withViewport } from '../../util/uiHelpers';
import { isArrayLength } from '../../util/genericHelpers';
import { selectWishlistIds } from '../../ducks/user.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';

import css from './FavoritesPage.module.css';

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M12 20s-7-4.4-8.8-8.6C1.8 8.1 4 5 7.1 5c1.9 0 3.4 1 4.2 2.4h1.4C13.5 6 15 5 16.9 5 20 5 22.2 8.1 20.8 11.4 19 15.6 12 20 12 20Z" />
  </svg>
);

export const FavoritesPageComponent = props => {
  const config = useConfiguration();
  const {
    listings,
    queryInProgress,
    queryListingsError,
    scrollingDisabled,
    intl,
    viewport,
    wishlistIds,
  } = props;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  const count = listings?.length || 0;
  const browseLabel = intl.formatMessage({ id: 'FavoritesPage.browse' });

  // One state at a time under the header: loading, error, empty, or the cards.
  const content = queryInProgress ? (
    <LoadingPlaceholder variant="cards" count={8} />
  ) : queryListingsError ? (
    <Notice tone="error" title={intl.formatMessage({ id: 'FavoritesPage.queryErrorTitle' })}>
      <p>
        <FormattedMessage id="FavoritesPage.queryError" />
      </p>
    </Notice>
  ) : count === 0 ? (
    <EmptyState
      icon={<HeartIcon />}
      title={intl.formatMessage({ id: 'FavoritesPage.emptyTitle' })}
      text={intl.formatMessage({ id: 'FavoritesPage.emptyText' })}
      action={
        <NamedLink name="SearchPage" className={css.primaryAction}>
          {browseLabel}
        </NamedLink>
      }
    />
  ) : (
    <FavoritesCardSection
      key={wishlistIds?.length}
      listings={listings}
      cardRenderSizes={cardRenderSizes}
    />
  );

  const marketplaceName = config.marketplaceName;
  const title = intl.formatMessage({ id: 'FavoritesPage.title' });
  const schemaTitle = intl.formatMessage({ id: 'FavoritesPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage(
    { id: 'FavoritesPage.schemaDescription' },
    { marketplaceName }
  );

  return (
    <Page title={schemaTitle} description={schemaDescription} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        showMobileNavBar
        topbar={<TopbarContainer viewport={viewport} isWhiteBgNav={true} />}
        footer={<FooterContainer showMobileNavBar />}
      >
        <PageContainer>
          <PageHeader
            title={title}
            subtitle={
              !queryInProgress && count > 0
                ? intl.formatMessage({ id: 'FavoritesPage.youHaveListings' }, { count })
                : null
            }
            actions={
              !queryInProgress && count > 0 ? (
                <NamedLink name="SearchPage" className={css.secondaryAction}>
                  {browseLabel}
                </NamedLink>
              ) : null
            }
          />
          {content}
        </PageContainer>
      </LayoutSingleColumn>
    </Page>
  );
};

FavoritesPageComponent.propTypes = {};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  // The reducer stores these under search* names; map them onto the query*
  // props the component reads (they were previously always undefined).
  const {
    currentPageResultIds,
    pagination,
    searchInProgress: queryInProgress,
    searchListingsError: queryListingsError,
    searchParams: queryParams,
  } = state.FavoritesPage;

  const wishlistIds = selectWishlistIds(state);
  const listings = isArrayLength(currentPageResultIds)
    ? getListingsById(state, currentPageResultIds)
    : [];

  const filteredListings =
    isArrayLength(wishlistIds) && isArrayLength(listings)
      ? listings.filter(l => wishlistIds.includes(l.id.uuid))
      : [];

  return {
    listings: filteredListings,
    currentUser,
    pagination,
    queryInProgress,
    queryListingsError,
    queryParams,
    scrollingDisabled: isScrollingDisabled(state),
    wishlistIds,
  };
};

const FavoritesPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl,
  withViewport
)(FavoritesPageComponent);

export default FavoritesPage;
